import { useMemo, useState } from 'react';
import { AutoComplete, Button, Checkbox, DatePicker, Input, Modal, Select, message } from 'antd';
import dayjs from 'dayjs';
import { CalendarClock, CalendarPlus, FileBadge, ShieldCheck, UserX } from 'lucide-react';
import { hrApi, useHr, type Result } from './hrStore';
import {
  CONSENT_CHANNELS, CONSENT_TEXT, INTERVIEW_ONLINE, INTERVIEW_STATUS, LICENSE_KIND, NO_SHOW_REASONS, STAGE, candById, empById, fmtD,
  interviewOverdue, isWorking, licenseBlocker, noShowCheck, pendingInterview, positionById, reqById, stageAt, toDay, validateCandidateLicense, validateInterview,
  type Candidate, type CandidateLicense, type Interview, type LicenseKind,
} from './hrLogic';
import { Badge, Field, Notes, fmtDateTime, useHrPerms } from './hrUi';
import { DateInput } from './hrModals';

/**
 * Các khối nghiệp vụ trong hồ sơ ứng viên: lịch phỏng vấn, giấy phép hành nghề, đồng ý xử lý dữ liệu,
 * và hộp thoại "không đến nhận việc" (dùng chung cho hồ sơ ứng viên và hồ sơ nhân sự).
 */

const done = (res: Result, ok: string) => {
  if (!res.ok) { Modal.error({ title: 'Chưa thực hiện được', content: res.errors?.join('\n') }); return false; }
  message.success(ok);
  res.warnings?.forEach(w => message.warning(w));
  return true;
};

const Box = ({ title, icon, extra, children }: { title: string; icon: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-2.5">
    <div className="flex items-center justify-between gap-2">
      <div className="text-sm font-semibold flex items-center gap-1.5">{icon}{title}</div>
      {extra}
    </div>
    {children}
  </div>
);

/** Giờ hẹn dạng "14:30 · Thứ năm 16/10" + nhãn còn bao lâu / quá giờ */
export function interviewWhen(i: Interview, now = Date.now()) {
  const d = dayjs(i.at);
  const days = d.startOf('day').diff(dayjs(now).startOf('day'), 'day');
  const label = `${d.format('HH:mm')} · ${days === 0 ? 'Hôm nay' : days === 1 ? 'Ngày mai' : days === -1 ? 'Hôm qua' : d.format('DD/MM')}`;
  const tone = i.status !== 'scheduled' ? '' : interviewOverdue(i, now) ? 'text-rose-600' : days === 0 ? 'text-amber-600' : 'text-sky-700';
  return { label, tone, overdue: interviewOverdue(i, now) };
}

/* ============================== Lịch phỏng vấn ============================== */

export function InterviewPanel({ c, canEdit }: { c: Candidate; canEdit: boolean }) {
  const s = useHr();
  const { me } = useHrPerms();
  const p = pendingInterview(c);
  const r = reqById(s, c.requisitionId);
  const [form, setForm] = useState<{ at?: number; place: string; interviewer: string; note: string } | null>(null);
  const past = [...(c.interviews || [])].filter(i => i.id !== p?.id).sort((a, b) => b.at - a.at);
  const branch = s.branches.find(b => b.id === r?.branchId);

  const placeOptions = useMemo(() => [...(branch ? [branch.name] : []), ...s.branches.filter(b => b.id !== branch?.id).map(b => b.name), ...INTERVIEW_ONLINE].map(v => ({ value: v })), [s.branches, branch]);
  const peopleOptions = useMemo(() => {
    const today = toDay();
    const people = s.employees.filter(e => isWorking(e, today) && positionById(s, e.positionId)?.level !== 'staff');
    people.sort((a, b) => Number(b.branchId === r?.branchId) - Number(a.branchId === r?.branchId) || a.name.localeCompare(b.name));
    return [{ value: me, label: `${me} (tôi)` }, ...people.map(e => ({ value: e.name, label: `${e.name} · ${positionById(s, e.positionId)?.name}` }))];
  }, [s, r?.branchId, me]);

  if (!(c.interviews || []).length && c.outcome !== 'open') return null;
  const open = c.outcome === 'open' && (c.stage === 'interview' || c.stage === 'skill');
  const v = form ? validateInterview(s, c, form) : { errors: [], warnings: [] };
  const startForm = () => setForm({
    at: p?.at, place: p?.place || branch?.name || '', note: p?.note || '',
    interviewer: p?.interviewer || (c.stage === 'interview' ? me : ''),
  });
  const save = () => { if (form && done(hrApi.scheduleInterview(c.id, { ...form, at: form.at! }, me), p ? 'Đã đổi lịch phỏng vấn' : 'Đã đặt lịch phỏng vấn')) setForm(null); };
  const noShow = (close: boolean) => Modal.confirm({
    title: close ? 'Ứng viên không đến — đóng hồ sơ?' : 'Ứng viên không đến — giữ hồ sơ để hẹn lại?',
    content: close ? 'Hồ sơ chuyển sang "Ứng viên từ chối" với lý do "Không đến phỏng vấn".' : 'Lần hẹn này ghi nhận vắng mặt, sau đó đặt lịch mới.',
    okText: 'Xác nhận', cancelText: 'Thôi', okButtonProps: close ? { danger: true } : undefined,
    onOk: () => { if (done(hrApi.interviewNoShow(c.id, me, close), 'Đã ghi nhận vắng mặt') && !close) startForm(); },
  });

  const w = p ? interviewWhen(p) : null;
  return (
    <Box title="Lịch phỏng vấn" icon={<CalendarClock size={15} className="text-[#3e91ab]" />}
      extra={open && canEdit && !form && <Button size="small" icon={<CalendarPlus size={13} />} onClick={startForm}>{p ? 'Đổi lịch' : 'Đặt lịch'}</Button>}>
      {open && !p && !form && <Notes warnings={[`Chưa hẹn lịch — ${STAGE[c.stage].label}`]} />}
      {p && w && !form && (
        <div className={`rounded-lg border px-3 py-2 text-sm ${w.overdue ? 'border-rose-200 bg-rose-50/60' : 'border-sky-200 bg-sky-50/60'}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <b className={w.tone}>{w.label}</b>
            <span className="text-xs text-gray-500">{STAGE[p.stage].short}</span>
          </div>
          <div className="text-gray-700 dark:text-gray-200">{p.place} · người phỏng vấn: <b>{p.interviewer}</b></div>
          {p.note && <div className="text-xs text-gray-500">{p.note}</div>}
          {w.overdue && <div className="text-xs text-rose-700 mt-1">Đã qua giờ hẹn — ghi kết quả bên dưới hoặc báo vắng mặt</div>}
          {canEdit && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {p.at <= Date.now() && <Button size="small" danger onClick={() => noShow(true)}>Không đến · đóng hồ sơ</Button>}
              {p.at <= Date.now() && <Button size="small" onClick={() => noShow(false)}>Không đến · hẹn lại</Button>}
              <Button size="small" type="text" onClick={() => done(hrApi.cancelInterview(c.id, me), 'Đã hủy lịch')}>Hủy lịch</Button>
            </div>
          )}
        </div>
      )}
      {form && (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Ngày giờ">
              <DatePicker className="w-full" showTime={{ format: 'HH:mm', minuteStep: 15 }} format="HH:mm DD/MM/YYYY" placeholder="Chọn ngày giờ"
                value={form.at ? dayjs(form.at) : null} disabledDate={d => d.isBefore(dayjs().startOf('day'))}
                onChange={d => setForm({ ...form, at: d ? d.second(0).millisecond(0).valueOf() : undefined })} />
            </Field>
            <Field label="Người phỏng vấn">
              <AutoComplete className="w-full" value={form.interviewer} options={peopleOptions} placeholder="Chọn hoặc nhập tên"
                filterOption={(q, o) => `${o?.label ?? ''}`.toLowerCase().includes(q.toLowerCase())} onChange={interviewer => setForm({ ...form, interviewer })} />
            </Field>
            <Field label="Địa điểm / hình thức" className="sm:col-span-2">
              <AutoComplete className="w-full" value={form.place} options={placeOptions} placeholder="Chi nhánh, phòng họp hoặc online" onChange={place => setForm({ ...form, place })} />
            </Field>
            <Field label="Ghi chú cho ứng viên / người phỏng vấn" className="sm:col-span-2">
              <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="VD: mang theo bằng cấp bản gốc, test tay nghề 30 phút" />
            </Field>
          </div>
          <Notes errors={form.at || form.place || form.interviewer ? v.errors : []} warnings={v.warnings} />
          <div className="flex gap-2">
            <Button type="primary" onClick={save} disabled={v.errors.length > 0}>{p ? 'Lưu lịch mới' : 'Đặt lịch'}</Button>
            <Button onClick={() => setForm(null)}>Thôi</Button>
          </div>
        </div>
      )}
      {past.length > 0 && (
        <div className="space-y-1">
          {past.map(i => (
            <div key={i.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <Badge className={INTERVIEW_STATUS[i.status].badge}>{INTERVIEW_STATUS[i.status].label}</Badge>
              <span>{STAGE[i.stage].short} · {dayjs(i.at).format('HH:mm DD/MM')} · {i.interviewer}</span>
              {i.resolvedNote && <span className="text-gray-400">({i.resolvedNote})</span>}
            </div>
          ))}
        </div>
      )}
    </Box>
  );
}

/* ============================== Giấy phép hành nghề ============================== */

type LicForm = Omit<CandidateLicense, 'verifiedBy' | 'verifiedAt'> & { verified: boolean };

export function LicensePanel({ c, need, canEdit }: { c: Candidate; need: LicenseKind; canEdit: boolean }) {
  const s = useHr();
  const { me } = useHrPerms();
  const today = toDay();
  const [form, setForm] = useState<LicForm | null>(null);
  const lic = c.license;
  const blocker = c.outcome === 'open' ? licenseBlocker(s, c, today) : null;
  const v = form ? validateCandidateLicense(form, today, c.offer?.startDate) : { errors: [], warnings: [] };
  const start = () => setForm({ kind: need, number: lic?.number || '', issuedAt: lic?.issuedAt || '', expiresAt: lic?.expiresAt, scope: lic?.scope || '', issuer: lic?.issuer || '', verified: !!lic?.verifiedAt });
  const save = () => {
    if (!form) return;
    const { verified, ...d } = form;
    if (done(hrApi.saveCandidateLicense(c.id, d, verified, me), verified ? 'Đã xác minh giấy phép' : 'Đã lưu giấy phép (chưa xác minh)')) setForm(null);
  };

  return (
    <Box title={LICENSE_KIND[need]} icon={<FileBadge size={15} className="text-[#3e91ab]" />}
      extra={c.outcome === 'open' && canEdit && !form && <Button size="small" onClick={start}>{lic ? (lic.verifiedAt ? 'Sửa' : 'Xác minh') : 'Nhập giấy phép'}</Button>}>
      {lic && !form && (
        <div className="text-sm space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <b>Số {lic.number}</b>
            {lic.verifiedAt
              ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200"><ShieldCheck size={12} /> Đã đối chiếu bản gốc</Badge>
              : <Badge className="bg-amber-50 text-amber-700 border-amber-200">Chưa đối chiếu bản gốc</Badge>}
          </div>
          <div className="text-xs text-gray-500">
            Cấp {fmtD(lic.issuedAt)}{lic.issuer ? ` · ${lic.issuer}` : ''}{lic.expiresAt ? ` · hết hạn ${fmtD(lic.expiresAt)}` : ' · không ghi thời hạn'}
          </div>
          {lic.scope && <div className="text-xs text-gray-500">Phạm vi: {lic.scope}</div>}
          {lic.verifiedAt && <div className="text-[11px] text-gray-400">Xác minh bởi {lic.verifiedBy} · {fmtDateTime(lic.verifiedAt)}</div>}
        </div>
      )}
      {!form && blocker && <Notes errors={[blocker]} />}
      {form && (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Số giấy phép"><Input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} placeholder="VD: 001234/HCM-GPHN" /></Field>
            <Field label="Nơi cấp"><Input value={form.issuer} onChange={e => setForm({ ...form, issuer: e.target.value })} placeholder="Sở Y tế / Bộ Y tế" /></Field>
            <Field label="Ngày cấp"><DateInput value={form.issuedAt} onChange={issuedAt => setForm({ ...form, issuedAt })} /></Field>
            <Field label="Ngày hết hạn (nếu có)"><DateInput value={form.expiresAt} onChange={expiresAt => setForm({ ...form, expiresAt: expiresAt || undefined })} min={today} /></Field>
            <Field label="Phạm vi hành nghề / chuyên môn" className="sm:col-span-2"><Input value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} /></Field>
          </div>
          <Checkbox checked={form.verified} onChange={e => setForm({ ...form, verified: e.target.checked })}>
            <span className="text-sm">Tôi đã đối chiếu bản gốc và tra cứu thông tin trên cổng của cơ quan cấp phép</span>
          </Checkbox>
          <Notes errors={form.number || form.issuedAt ? v.errors : []} warnings={v.warnings} />
          <div className="flex gap-2">
            <Button type="primary" onClick={save} disabled={v.errors.length > 0}>{form.verified ? 'Lưu & xác minh' : 'Lưu, xác minh sau'}</Button>
            <Button onClick={() => setForm(null)}>Thôi</Button>
          </div>
        </div>
      )}
    </Box>
  );
}

/* ============================== Đồng ý xử lý dữ liệu ============================== */

export function ConsentLine({ c, canEdit }: { c: Candidate; canEdit: boolean }) {
  const { me } = useHrPerms();
  const [channel, setChannel] = useState<string>();
  if (c.anonymizedAt) return <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2">Dữ liệu cá nhân đã xóa ngày {fmtDateTime(c.anonymizedAt)} — chỉ giữ số liệu thống kê.</div>;
  if (c.consent) return <div className="text-[11px] text-gray-400 flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-500" />Đồng ý xử lý dữ liệu: {c.consent.channel} · {fmtDateTime(c.consent.at)} · {c.consent.by}</div>;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-2">
      <div><b>Chưa ghi nhận đồng ý xử lý dữ liệu cá nhân.</b> {CONSENT_TEXT}</div>
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Select size="small" className="min-w-[240px]" placeholder="Ứng viên xác nhận qua…" value={channel} onChange={setChannel} options={CONSENT_CHANNELS.map(x => ({ value: x, label: x }))} />
          <Button size="small" disabled={!channel} onClick={() => done(hrApi.recordConsent(c.id, channel!, me), 'Đã ghi nhận đồng ý')}>Ghi nhận</Button>
        </div>
      )}
    </div>
  );
}

/** Ứng viên yêu cầu xóa dữ liệu / rút lại đồng ý */
export function eraseCandidate(c: Candidate, me: string, after?: () => void) {
  Modal.confirm({
    title: `Xóa dữ liệu cá nhân của ${c.name}?`,
    content: <div className="text-sm space-y-1">
      <p>Xóa họ tên, số điện thoại, email, CCCD, giấy phép và các nhận xét. Chỉ giữ nguồn, bước, lý do để thống kê. Không khôi phục được.</p>
      {c.outcome === 'open' && <p>Hồ sơ đang xử lý sẽ đóng với lý do “Ứng viên yêu cầu xóa dữ liệu”.</p>}
    </div>,
    okText: 'Xóa dữ liệu', okButtonProps: { danger: true }, cancelText: 'Thôi',
    onOk: () => { if (done(hrApi.eraseCandidate(c.id, me), 'Đã xóa dữ liệu cá nhân')) after?.(); },
  });
}

/* ============================== Không đến nhận việc ============================== */

export function NoShowModal({ empId, onClose, onDone }: { empId: string; onClose: () => void; onDone?: (candidateId: string) => void }) {
  const s = useHr();
  const { me } = useHrPerms();
  const [reason, setReason] = useState(NO_SHOW_REASONS[0]);
  const e = empById(s, empId);
  const c = candById(s, e?.candidateId);
  const r = reqById(s, c?.requisitionId);
  if (!e || !c) return null;
  const chk = noShowCheck(s, e, toDay());
  const reopened = r?.status === 'closed' ? s.candidates.filter(x => x.requisitionId === r.id && x.outcome === 'withdrawn' && x.closeReason === 'Vị trí đã đủ người' && (x.closedAt || 0) >= (stageAt(c, 'hired') || 0)).length : 0;
  const save = () => {
    const res = hrApi.cancelHire(e.id, reason, me);
    if (done(res, `Đã hủy nhận việc của ${e.name}`)) { onClose(); onDone?.(res.id!); }
  };
  return (
    <Modal open title={<span className="flex items-center gap-2"><UserX size={18} className="text-rose-500" />{e.name} không đến nhận việc</span>} onCancel={onClose} width={560}
      footer={[<Button key="c" onClick={onClose}>Thôi</Button>, <Button key="s" danger type="primary" disabled={!chk.ok} onClick={save}>Xác nhận không đến</Button>]}>
      <div className="space-y-3 text-sm">
        <Notes errors={chk.ok ? [] : [chk.reason!]} />
        <Field label="Lý do"><Select className="w-full" value={reason} onChange={setReason} options={NO_SHOW_REASONS.map(x => ({ value: x, label: x }))} /></Field>
        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300 space-y-1">
          <li>Xóa hồ sơ nhân sự <b>{e.code}</b> (ngày nhận việc {fmtD(e.hireDate)}) và thỏa thuận thử việc đã tạo — không tính vào tỷ lệ nghỉ sớm.</li>
          <li>Ứng viên chuyển sang “Ứng viên từ chối”, offer ghi nhận “Không đến nhận việc”.</li>
          {r?.status === 'closed' && <li>Mở lại yêu cầu <b>{r.code}</b>{reopened ? <> và đưa <b>{reopened}</b> ứng viên bị đóng do đủ người quay lại quy trình</> : null}.</li>}
        </ul>
      </div>
    </Modal>
  );
}
